import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/guardrails/')({
  beforeLoad: () => {
    return {
      pageTitle: 'Guardrails',
    };
  },
  loader: () => {
    return {
      pageTitle: 'Guardrails',
    };
  },
  component: Guardrails,
});

function Guardrails() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [deleteGuardrailId, setDeleteGuardrailId] = useState<string | null>(
    null
  );

  const { data } = useQuery(trpc.guardrail.list.queryOptions());

  const { mutate: deleteGuardrail, isPending: isDeleting } = useMutation(
    trpc.guardrail.deleteOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Guardrail deleted',
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey: [['guardrail', 'list']],
        });
        setDeleteGuardrailId(null);
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to delete guardrail';
        toaster.error({
          title: 'Failed to delete guardrail',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const handleDelete = (guardrailId: string) => {
    setDeleteGuardrailId(guardrailId);
  };

  const confirmDelete = () => {
    if (deleteGuardrailId) {
      deleteGuardrail({ id: deleteGuardrailId });
    }
  };

  const filteredGuardrails = data?.filter((guardrail) =>
    searchValue
      ? guardrail.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        guardrail.guardrailIdentifier
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      : true
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex justify="space-between" align="center">
          <TextInput
            placeholder="Search by name or identifier..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            maxW="300px"
          />
          <Button onClick={() => navigate({ to: '/guardrails/add' })}>
            Add Guardrail
          </Button>
        </Flex>
        <Table.Root size="lg" width="100%">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Identifier</Table.ColumnHeader>
              <Table.ColumnHeader>Version</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredGuardrails?.map((guardrail) => (
              <Table.Row key={guardrail.id}>
                <Table.Cell>{guardrail.name}</Table.Cell>
                <Table.Cell>{guardrail.guardrailIdentifier}</Table.Cell>
                <Table.Cell>{guardrail.guardrailVersion}</Table.Cell>
                <Table.Cell textAlign="end">
                  <Flex gap={2} justify="flex-end">
                    <Button
                      iconName="pen"
                      variant="ghostNeutral"
                      size="sm"
                      onClick={() =>
                        navigate({ to: `/guardrails/${guardrail.id}` })
                      }
                    />
                    <Button
                      iconName="trashcan"
                      variant="ghostDestructive"
                      size="sm"
                      onClick={() => handleDelete(guardrail.id)}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {filteredGuardrails?.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            No guardrails found
          </Flex>
        )}
      </Flex>
      <DeleteDialog
        isOpen={!!deleteGuardrailId}
        title="Delete Guardrail"
        description={
          deleteGuardrailId
            ? `Are you sure you want to delete this guardrail? This action cannot be undone.`
            : ''
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteGuardrailId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
